(function () {
  'use strict';

  if (customElements.get('amelie-bundle')) return;

  class AmelieBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      // Back from checkout (straight-to-checkout mode): the browser can restore
      // this page from its back/forward cache with the button still busy.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !this.redirectingToCheckout) return;
        this.redirectingToCheckout = false;
        this.setBusy(false);
      });
      this.busy = false;
      this.quantity = Number(this.querySelector('[data-bundle-tiers] input:checked')?.value || 2);
      this.bags = Array.from(this.querySelectorAll('[data-bundle-bag]'));
      this.tiers = Array.from(this.querySelectorAll('[data-bundle-tier]'));
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.errorElement = this.querySelector('[data-bundle-error]');
      this.buttonLabel = this.querySelector('[data-bundle-button-label]');
      this.totalElement = this.querySelector('[data-bundle-total]');
      this.savingElement = this.querySelector('[data-bundle-saving]');
      this.summaryElement = this.querySelector('.amelie-bundle__summary');
      this.stockElement = this.querySelector('[data-bundle-stock]');
      this.recentBags = [];

      try {
        this.product = JSON.parse(this.querySelector('[data-bundle-product]').textContent);
      } catch (error) {
        this.showError(this.dataset.networkText);
        return;
      }

      this.discountTwo = Number(this.dataset.discountTwo || 0);
      this.discountThree = Number(this.dataset.discountThree || 0);
      this.excludeCombinedValues = this.dataset.excludeCombinedValues === 'true';
      this.variants = this.product.variants.filter((variant) => this.isUnitVariant(variant));
      // A dropdown (size, shoe size) is never filled in for the shopper: each
      // item keeps "no variant" until every dropdown has been set.
      this.hasDropdowns = !!this.querySelector('[data-bundle-bag] select');

      this.bags.forEach((bag, index) => this.initializeBag(bag, index));
      if (this.stockElement) this.drawShownStock();
      this.bindEvents();
      this.update();
    }

    isUnitVariant(variant) {
      var position = Number(this.product.color_option_position || 0);
      if (!this.excludeCombinedValues || position < 1) return true;
      return String(variant.options[position - 1] || '').indexOf('+') === -1;
    }

    initializeBag(bag, index) {
      var available = this.variants.filter((variant) => variant.available);

      if (!this.hasDropdowns) {
        bag.selectedVariant = available[index % available.length] || available[0] || this.variants[0] || null;
        bag.priceVariant = bag.selectedVariant;
        this.syncControls(bag);
        this.updateAvailability(bag);
        return;
      }

      // Start each item on its own color, as on a color-only product, and
      // leave the dropdowns on their prompt.
      var colorIndex = Number(this.product.color_option_position || 0) - 1;
      if (colorIndex >= 0) {
        var colors = [];
        available.forEach((variant) => {
          if (colors.indexOf(variant.options[colorIndex]) === -1) colors.push(variant.options[colorIndex]);
        });
        var color = colors.length ? colors[index % colors.length] : null;
        var wrapper = bag.querySelector('[data-bundle-option="' + colorIndex + '"]');
        if (wrapper) {
          Array.from(wrapper.querySelectorAll('input[type="radio"]')).forEach((radio) => {
            radio.checked = radio.value === color;
          });
        }
      }
      this.resolveBag(bag);
      this.updateAvailability(bag);
    }

    /* Read the item's controls. `selectedVariant` is the variant that will be
       bought and exists only once every option is chosen; `priceVariant` is a
       variant matching the choices so far, used to show a price meanwhile. */
    resolveBag(bag) {
      var wanted = this.selectedOptions(bag);
      var complete = wanted.every((value) => !!value);
      var matches = (variant) => variant.options.every((value, index) => !wanted[index] || value === wanted[index]);

      bag.selectedVariant = complete
        ? this.variants.find((variant) => variant.available && matches(variant)) || null
        : null;
      bag.priceVariant =
        bag.selectedVariant ||
        this.variants.find((variant) => variant.available && matches(variant)) ||
        this.variants.find(matches) ||
        this.variants[0] ||
        null;

      Array.from(bag.querySelectorAll('select')).forEach((select) => {
        select.classList.toggle('is-unset', !select.value);
        if (select.value) select.classList.remove('is-missing');
      });
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        var tierInput = event.target.closest('[data-bundle-tier] input');
        if (tierInput) {
          this.quantity = Number(tierInput.value);
          this.update();
          return;
        }

        var bag = event.target.closest('[data-bundle-bag]');
        var option = event.target.closest('[data-bundle-option]');
        if (!bag || !option) return;

        var optionIndex = Number(option.dataset.bundleOption);
        var value = event.target.value;
        this.markRecent(bag);
        this.selectVariant(bag, optionIndex, value);
        this.update();
      });

      this.submitButton.addEventListener('click', () => this.addBundle());
    }

    selectedOptions(bag) {
      return this.product.options.map((option, index) => {
        var wrapper = bag.querySelector('[data-bundle-option="' + index + '"]');
        if (!wrapper) return null;
        var checked = wrapper.querySelector('input:checked');
        var select = wrapper.querySelector('select');
        return checked ? checked.value : select ? select.value || null : null;
      });
    }

    selectVariant(bag, changedIndex, changedValue) {
      if (this.hasDropdowns) {
        // The control already holds the new value; nothing else is changed for the shopper.
        this.resolveBag(bag);
        this.updateAvailability(bag);
        return;
      }

      var wanted = this.selectedOptions(bag);
      wanted[changedIndex] = changedValue;
      var exact = this.variants.find((variant) =>
        variant.available && variant.options.every((value, index) => value === wanted[index])
      );

      if (!exact) {
        exact = this.variants.find(
          (variant) => variant.available && variant.options[changedIndex] === changedValue
        );
      }

      bag.selectedVariant = exact || null;
      this.syncControls(bag);
      this.updateAvailability(bag);
    }

    syncControls(bag) {
      if (!bag.selectedVariant) return;
      bag.selectedVariant.options.forEach((value, index) => {
        var wrapper = bag.querySelector('[data-bundle-option="' + index + '"]');
        if (!wrapper) return;
        var radios = Array.from(wrapper.querySelectorAll('input[type="radio"]'));
        var select = wrapper.querySelector('select');
        radios.forEach((radio) => {
          radio.checked = radio.value === value;
        });
        if (select) select.value = value;
      });
    }

    updateAvailability(bag) {
      var selected = bag.selectedVariant ? bag.selectedVariant.options.slice() : this.selectedOptions(bag);

      this.product.options.forEach((option, optionIndex) => {
        var wrapper = bag.querySelector('[data-bundle-option="' + optionIndex + '"]');
        if (!wrapper) return;

        var values = Array.from(wrapper.querySelectorAll('input[type="radio"], option')).filter(
          (control) => control.value !== ''
        );
        values.forEach((control) => {
          var possible = this.variants.some((variant) => {
            if (!variant.available || variant.options[optionIndex] !== control.value) return false;
            // An option that is still open does not rule anything out.
            return variant.options.every(
              (value, index) => index === optionIndex || !selected[index] || value === selected[index]
            );
          });

          control.disabled = !possible;
          var swatch = control.closest('.amelie-bundle__swatch');
          if (swatch) swatch.classList.toggle('is-disabled', !possible);
        });
      });
    }

    discountFor(quantity) {
      if (quantity === 2) return this.discountTwo;
      if (quantity === 3) return this.discountThree;
      return 0;
    }

    selectionFor(quantity) {
      return this.bags.slice(0, quantity).map((bag) => bag.selectedVariant);
    }

    totalsFor(quantity) {
      var selections = this.bags.slice(0, quantity).map((bag) => bag.selectedVariant || bag.priceVariant);
      var subtotal = selections.reduce((sum, variant) => sum + (variant ? Number(variant.price) : 0), 0);
      var discount = this.discountFor(quantity);
      var saving = Math.round((subtotal * discount) / 100);
      return { subtotal: subtotal, saving: saving, total: subtotal - saving };
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.product.currency,
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2) + ' ' + this.product.currency;
      }
    }

    update() {
      this.tiers.forEach((tier) => {
        var quantity = Number(tier.dataset.bundleTier);
        var selected = quantity === this.quantity;
        tier.classList.toggle('is-selected', selected);

        var totals = this.totalsFor(quantity);
        var price = tier.querySelector('[data-tier-price]');
        var compare = tier.querySelector('[data-tier-compare]');
        var saving = tier.querySelector('[data-tier-saving]');
        if (price) price.textContent = this.formatMoney(totals.total);
        if (compare) {
          compare.textContent = this.formatMoney(totals.subtotal);
          compare.hidden = totals.saving === 0;
        }
        if (saving) {
          saving.hidden = totals.saving === 0;
          var savingValue = saving.querySelector('span');
          if (savingValue) savingValue.textContent = this.formatMoney(totals.saving);
        }
        var unit = tier.querySelector('[data-tier-unit]');
        if (unit && quantity > 0) {
          unit.textContent = this.formatMoney(Math.round(totals.total / quantity)) + ' ' + (this.dataset.eachText || 'each');
        }
      });

      this.bags.forEach((bag, index) => {
        bag.hidden = index >= this.quantity;
        var selectedValue = bag.querySelector('[data-bundle-selected]');
        var colorIndex = Number(this.product.color_option_position || 1) - 1;
        if (selectedValue) {
          var chosen = bag.selectedVariant ? bag.selectedVariant.options : this.selectedOptions(bag);
          selectedValue.textContent = chosen[colorIndex] || '';
        }
      });

      var totals = this.totalsFor(this.quantity);
      this.totalElement.textContent = this.formatMoney(totals.total);
      if (this.savingElement) this.savingElement.textContent = this.formatMoney(totals.saving);
      if (this.summaryElement) this.summaryElement.hidden = true;
      this.clearError();
      this.updateStock();
    }

    /* Few-left line ([data-bundle-stock]). Shows a number from 1 to 8 per
       variant, drawn once per page view, so going back to a size shows the
       same number. Sizes of one colour each get a different number.
       Deliberately not linked to Shopify inventory: the store keeps track
       of stock itself. */
    sizeOptionIndex() {
      var colorIndex = Number(this.product.color_option_position || 0) - 1;
      return this.product.options.findIndex((option, index) => index !== colorIndex && /size/i.test(option.name));
    }

    drawShownStock() {
      var max = 8;
      // With a single variant every item in the bundle is that variant, so
      // the number starts at the largest bundle and never moves with the tier.
      var min = this.variants.length === 1 ? Math.min(this.bags.length, max) : 1;
      var sizeIndex = this.sizeOptionIndex();
      var groups = {};
      this.variants.forEach((variant) => {
        var key = variant.options.filter((value, index) => index !== sizeIndex).join('\u0000');
        (groups[key] = groups[key] || []).push(variant);
      });
      this.shownStock = {};
      Object.keys(groups).forEach((key) => {
        var pool = [];
        for (var n = min; n <= max; n++) pool.push(n);
        for (var i = pool.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var swap = pool[i];
          pool[i] = pool[j];
          pool[j] = swap;
        }
        groups[key].forEach((variant, index) => {
          this.shownStock[variant.id] =
            index < pool.length ? pool[index] : min + Math.floor(Math.random() * (max - min + 1));
        });
      });
    }

    markRecent(bag) {
      this.recentBags = [bag].concat(this.recentBags.filter((other) => other !== bag));
    }

    /* "Blue Woven" or "Blue Woven, size M": option values only, so no SKU,
       ID or "Default Title". Options with a single value are left out; any
       size option (Size, Shoe Size (US)) reads as "size". */
    variantDisplayName(variant) {
      if (this.product.single_variant) return '';
      var sizeIndex = this.sizeOptionIndex();
      var names = [];
      var size = '';
      variant.options.forEach((value, index) => {
        var option = this.product.options[index];
        if (!value || !option || option.values.length < 2) return;
        if (index === sizeIndex) {
          size = 'size ' + value;
        } else {
          names.push(value);
        }
      });
      if (size) names.push(size);
      return names.join(', ');
    }

    /* The line follows the item the shopper chose last; until then the first
       item with a complete choice. It always names a variant that is in the
       bundle the button adds. */
    updateStock() {
      if (!this.stockElement || !this.shownStock) return;
      var element = this.stockElement;
      var visible = this.bags.slice(0, this.quantity);
      var order = this.recentBags.filter((bag) => visible.indexOf(bag) !== -1).concat(visible);
      var bag = order.find((candidate) => candidate.selectedVariant && candidate.selectedVariant.available);
      var variant = bag ? bag.selectedVariant : null;

      if (!variant || !this.shownStock[variant.id]) {
        element.textContent = '';
        element.hidden = true;
        delete element.dataset.stockVariant;
        return;
      }

      // Never fewer left than this bundle already holds of the same variant.
      var inBundle = visible.filter(
        (other) => other.selectedVariant && other.selectedVariant.id === variant.id
      ).length;
      var count = Math.max(this.shownStock[variant.id], inBundle);
      var name = this.variantDisplayName(variant);
      var fill = (template, fallback) =>
        String(template || fallback).replace(/\[count\]/g, count).replace(/\[variant\]/g, name);
      var text = !name
        ? fill(this.dataset.stockSingle, 'Only [count] left')
        : count === 1
          ? fill(this.dataset.stockOne, 'Only 1 left in [variant]')
          : fill(this.dataset.stockText, '[count] left in [variant]');

      var dot = document.createElement('span');
      dot.className = 'amelie-bundle__stock-dot';
      dot.setAttribute('aria-hidden', 'true');
      var label = document.createElement('span');
      label.textContent = text;
      element.replaceChildren(dot, label);
      element.dataset.stockVariant = String(variant.id);
      element.hidden = false;
    }

    setBusy(state) {
      this.busy = state;
      this.submitButton.disabled = state;
      this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
      this.buttonLabel.textContent = state ? this.dataset.loadingText : this.dataset.buttonText;
    }

    showError(message) {
      this.errorElement.textContent = message || this.dataset.networkText;
      this.errorElement.hidden = false;
    }

    clearError() {
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    addBundle() {
      if (this.busy) return;
      this.clearError();

      var selections = this.selectionFor(this.quantity);
      if (selections.length !== this.quantity || selections.some((variant) => !variant)) {
        this.showError(this.dataset.missingText);
        // Point at the dropdowns that still need a choice.
        var open = [];
        this.bags.slice(0, this.quantity).forEach((bag) => {
          Array.from(bag.querySelectorAll('select')).forEach((select) => {
            if (!select.value) open.push(select);
          });
        });
        open.forEach((select) => select.classList.add('is-missing'));
        if (open[0]) open[0].focus();
        return;
      }
      if (selections.some((variant) => !variant.available)) {
        this.showError(this.dataset.unavailableText);
        return;
      }

      var grouped = selections.reduce((items, variant) => {
        var key = String(variant.id);
        if (!items[key]) items[key] = { id: variant.id, quantity: 0 };
        items[key].quantity += 1;
        return items;
      }, {});
      var items = Object.keys(grouped).map((key) => grouped[key]);
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : [];
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

      if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(this.submitButton);
      this.setBusy(true);

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: items,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json().then((payload) => ({ ok: response.ok, payload: payload })))
        .then((result) => {
          if (!result.ok || result.payload.status) {
            throw new Error(result.payload.description || result.payload.message || this.dataset.unavailableText);
          }

          if (window.oliviaSkipCart) {
            // Skip cart: the bundle is in the cart, go straight to checkout.
            this.redirectingToCheckout = true;
            window.location.href = '/checkout';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'amelie-bundle',
              productVariantId: selections[0].id,
              cartData: result.payload,
            });
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(result.payload);
          } else if (window.routes && window.routes.cart_url) {
            window.location.assign(window.routes.cart_url);
          }
        })
        .catch((error) => {
          this.showError(error && error.message ? error.message : this.dataset.networkText);
        })
        .finally(() => {
          if (!this.redirectingToCheckout) this.setBusy(false);
        });
    }
  }

  customElements.define('amelie-bundle', AmelieBundle);
})();
